export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: "api/v1/auth/login",
    REGISTER: "api/v1/auth/register",
    LOGOUT: "api/v1/auth/logout",
    REFRESH_TOKEN: "api/v1/auth/refresh",
    ME: "api/v1/auth/me",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
    /** Invite-based account activation (GET: token → profile snapshot, POST: set password) */
    ACTIVATE_ACCOUNT: "api/v1/users/activate-account",
  },

  // Dashboard
  DASHBOARD: {
    STUDENT: "/dashboard/student",
    TEACHER: "/dashboard/teacher",
    ADMIN: "/dashboard/admin",
  },

  // Tenant users directory (auth-scoped)
  TENANT_USERS: {
    LIST: "api/v1/tenants/users",
    CREATE: "api/v1/tenants/users",
    DETAIL: (userId: string) => `api/v1/tenants/users/${userId}`,
    UPDATE: (userId: string) => `api/v1/tenants/users/${userId}`,
    DELETE: (userId: string) => `api/v1/tenants/users/${userId}`,
  },

  // Tenant users (admin dashboards, analytics, etc.)
  USERS: {
    /** List users; used by the Teachers landing page (query params: first_name, email, status, role, page, page_size) */
    LIST: "api/v1/users",
    /**
     * Create a user. The Add Teacher and Add Student forms post here with a fixed role
     * ("teacher" / "student") and no tenant field
     * (the backend scopes by the caller); the platform-admin flow sends tenantId in the body.
     */
    CREATE: "api/v1/users",
    /** Platform admin: get user details by id (includes all tenant memberships) */
    DETAIL: (userId: string) => `api/v1/users/${userId}`,
    /**
     * Update a user; the Edit Teacher and Edit Student forms PATCH here (verbs:
     * TEACHER_UPDATE_METHOD, STUDENT_UPDATE_METHOD).
     */
    UPDATE: (userId: string) => `api/v1/users/${userId}`,
    /** Delete a user; the Students and Teachers list delete actions call this. */
    DELETE: (userId: string) => `api/v1/users/${userId}`,
    /** POST (no body): re-send the activation (password setup) email of a pending user. */
    PASSWORD_SETUP_EMAIL: (userId: string) =>
      `api/v1/users/${userId}/password-setup-email`,
  },

  // User (profile, learning summary, subjects, recent courses, curriculum, etc.)
  USER: {
    /** GET course + activities list (includes course.sourcePdf for textbook) */
    CHAT: "api/v1/user/chat",
    CHAT_HISTORY: "api/v1/user/chat/history",
    SUBJECTS: "/api/v1/global",
  },

  // Students (tenant-scoped users)
  STUDENTS: {
    LIST_FOR_TENANT: (tenantId: string) => `api/v1/tenants/${tenantId}/users`,
    CREATE_FOR_TENANT: (tenantId: string) => `api/v1/tenants/${tenantId}/users`,
    UPDATE_FOR_TENANT_USER: (tenantId: string, userId: string) =>
      `api/v1/tenants/${tenantId}/users/${userId}`,
    DELETE_FOR_TENANT_USER: (tenantId: string, userId: string) =>
      `api/v1/tenants/${tenantId}/users/${userId}`,
  },

  /**
   * Global tenants directory (admin): list/detail/create/update/delete CRUD.
   * The HTTP verb used for UPDATE is chosen in features/school/services/tenantsService.ts.
   */
  TENANTS: {
    LIST: "api/v1/tenants",
    SUMMARY: "api/v1/tenants/summary",
    /**
     * Tenant dashboard (all GET, bearer token). Unknown query params are rejected with a
     * 400, so services send only the documented ones. A tenant admin never sends `tenant_id`.
     */
    DASHBOARD: {
      /** `tenant_id?`, `days?` (7 or 30) */
      OVERVIEW: "api/v1/tenants/dashboard/overview",
      /**
       * `tenant_id?`, `days?`, `status?`, `search?` (≤100 chars), `sort?`, `order?`, `page?`,
       * `page_size?` (1–100)
       */
      INSTITUTIONS: "api/v1/tenants/dashboard/institutions",
      /** `tenant_id?`, `months?` (6 or 12) */
      TRENDS: "api/v1/tenants/dashboard/trends",
    },
    CREATE: "api/v1/tenants",
    DETAIL: (id: string) => `api/v1/tenants/${id}`,
    UPDATE: (id: string) => `api/v1/tenants/${id}`,
    DELETE: (id: string) => `api/v1/tenants/${id}`,
  },
  /**
   * Global institutions directory (admin): list/detail/create/update/delete CRUD.
   * The HTTP verb used for UPDATE is chosen in features/institutions/services/institutionsService.ts.
   */
  INSTITUTIONS: {
    LIST: "api/v1/institutions",
    CREATE: "api/v1/institutions",
    DETAIL: (id: string) => `api/v1/institutions/${id}`,
    UPDATE: (id: string) => `api/v1/institutions/${id}`,
    DELETE: (id: string) => `api/v1/institutions/${id}`,
    /**
     * Institute dashboard (all GET, bearer token). Unknown query params are rejected with a
     * 400, so services send only the documented ones. A school admin never sends `institute_id`.
     */
    DASHBOARD: {
      /** `institute_id?` */
      OVERVIEW: "api/v1/institutions/dashboard/overview",
      /** `institute_id?`, `from?`, `to?` (YYYY-MM-DD), `threshold?` (1–100) */
      ATTENDANCE: "api/v1/institutions/dashboard/attendance",
      /** `institute_id?`, `class_section_id?`, `board_subject_id?` */
      ACADEMICS: "api/v1/institutions/dashboard/academics",
      /** `institute_id?`, `days?` (7 or 30) */
      ENGAGEMENT: "api/v1/institutions/dashboard/engagement",
      /** `institute_id?`, `days?` (7 or 30) */
      DOUBTS: "api/v1/institutions/dashboard/doubts",
      /** `institute_id?`, `page?`, `page_size?` (1–100) */
      TEACHERS: "api/v1/institutions/dashboard/teachers",
    },
  },

  /**
   * Announcements admin API. LIST and CREATE are specified by the product owner.
   * PIN (POST = pin, DELETE = unpin) and UPDATE (PATCH, no `/admin` segment) are specified.
   * The DELETE url and verb are ASSUMPTIONS: confirm with the backend, then edit this block only.
   */
  ANNOUNCEMENTS: {
    LIST: "api/v1/announcements/admin", // GET  (specified)
    CREATE: "api/v1/announcements/admin", // POST (specified)
    SUMMARY: "api/v1/announcements/admin/summary", // GET (specified): tab counts
    UPDATE: (id: number | string) => `api/v1/announcements/${id}`, // PATCH (specified)
    DELETE: (id: number | string) => `api/v1/announcements/admin/${id}`, // ASSUMED
    PIN: (id: number | string) => `api/v1/announcements/admin/${id}/pin`, // POST pins, DELETE unpins (specified)
    METHODS: { UPDATE: "patch", DELETE: "delete" } as const, // UPDATE specified, DELETE ASSUMED
  },

  /**
   * Lookup helpers used by the Add Student form's cascading dropdowns.
   * BOARD_CLASSES takes the query param `board_id`; CLASS_SECTIONS takes `board_class_id`
   * (the id of a class returned by BOARD_CLASSES).
   */
  /**
   * Teaching assignments of one class section (its id is in the path). Query params:
   * `teacher_id`, `institute_id`. Body: `{ teaching_assignments: [{ board_subject_id, subject_name, ... }] }`.
   */
  TEACHING_ASSIGNMENTS: {
    /**
     * All teacher ↔ class mappings of one institute (Teachers → Class mapping page). Query
     * params: `institute_id` (required), `class_id?`, `section_id?`, `teacher_id?`, and
     * `page?` for paging. Body: `{ teaching_assignments: [...], pagination }`.
     */
    LIST: "api/v1/teacher/teaching-assignments",
    /** POST: body `{ teacher_id, assignments: [{ class_section_id, board_subject_id?, is_class_teacher }] }` (bulk). */
    CREATE: "api/v1/teacher/teaching-assignments",
    /** PATCH (body `{ class_id, section_id, board_subject_id, is_class_teacher }`) / DELETE. */
    DETAIL: (id: number | string) => `api/v1/teacher/teaching-assignments/${id}`,
    BY_SECTION: (sectionId: string) =>
      `api/v1/teacher/sections/${sectionId}/teaching-assignments`,
  },

  /** Academic Setup (school admin): streams of the institute. */
  ACADEMIC_SETUP: {
    /**
     * GET `?institute_id=` -> `{ streams: [{ id, name, board, class, subjects: [...] }] }` (no
     * pagination). POST body: `{ name, board_id, class_id, institute_id, subjects: [ids] }`.
     */
    STREAMS: "api/v1/institutions/streams",
    /** PATCH (same body as POST) / DELETE one stream. */
    STREAM: (id: number | string) => `api/v1/institutions/streams/${id}`,
    /**
     * GET `?institute_id=&page=` -> `{ class_sections: [{ id, section_label, academic_year,
     * board, class }], pagination }`. POST body: `{ board_id, class_id, section_label, institute_id }`.
     */
    CLASS_SECTIONS: "api/v1/institutions/class-sections",
    /** PATCH (same body as POST) / DELETE one section. */
    CLASS_SECTION: (id: number | string) => `api/v1/institutions/class-sections/${id}`,
    /**
     * GET `?institute_id=&page=` -> `{ academic_terms: [{ id, name, term_number, code, start_date,
     * end_date, academic_year_start, academic_year_end, status }], pagination }`. POST body:
     * `{ name, term_number, code, start_date, end_date, institute_id }`.
     */
    ACADEMIC_TERMS: "api/v1/institutions/academic-terms",
    /** PATCH (POST body without `institute_id`) / DELETE one term. */
    ACADEMIC_TERM: (id: number | string) => `api/v1/institutions/academic-terms/${id}`,
  },

  /**
   * Timetable admin API (school admin). Base path `/api/v1/timetable`; see
   * docs/timetable/timetable-admin-apis.md. Everything is scoped to one academic term.
   */
  TIMETABLE: {
    /** GET: blank workbook (.xlsx). */
    TEMPLATE: "api/v1/timetable/template",
    /** POST multipart `{ file, term_id }` -> `{ upload, report }`. GET `?term_id=&status=&page=&page_size=`. */
    UPLOADS: "api/v1/timetable/uploads",
    /** GET `{ upload, report }` / DELETE one upload. */
    UPLOAD: (id: number | string) => `api/v1/timetable/uploads/${id}`,
    /** GET original .xlsx of an upload. */
    UPLOAD_FILE: (id: number | string) => `api/v1/timetable/uploads/${id}/file`,
    /** GET `?class_code=` preview grid from the stored upload payload. */
    UPLOAD_PREVIEW: (id: number | string) => `api/v1/timetable/uploads/${id}/preview`,
    /** POST re-run checks on the stored payload. */
    UPLOAD_REVALIDATE: (id: number | string) =>
      `api/v1/timetable/uploads/${id}/revalidate`,
    /** POST `{ effective_from, note? }` -> `{ version, warnings_published_with }`. */
    UPLOAD_PUBLISH: (id: number | string) =>
      `api/v1/timetable/uploads/${id}/publish`,
    /** GET `?term_id=&page=&page_size=` -> `{ versions, pagination }`. */
    VERSIONS: "api/v1/timetable/versions",
    /** GET `{ version, term, bell, classes, teachers }`. */
    VERSION: (id: number | string) => `api/v1/timetable/versions/${id}`,
    /** GET `?class_section_id=|teacher_id=` weekly grid of one class or teacher. */
    VERSION_GRID: (id: number | string) =>
      `api/v1/timetable/versions/${id}/grid`,
    /** GET `?term_id=|version_id=` bell schedule. */
    BELL_SCHEDULE: "api/v1/timetable/bell-schedule",
    /** GET `?term_id=&from=&to=` -> `{ holidays }`. */
    HOLIDAYS: "api/v1/timetable/holidays",
    /** GET one holiday. */
    HOLIDAY: (id: number | string) => `api/v1/timetable/holidays/${id}`,
  },

  HELPERS: {
    BOARD_CLASSES: "api/v1/helpers/get-board-classes",
    CLASS_SECTIONS: "api/v1/helpers/get-class-sections",
    /** Query params: `board_id`, `class_id` (the id returned by BOARD_CLASSES). */
    SUBJECTS: "api/v1/helpers/get-subjects",
    /** Flat body: `{ categories: [{ id, name, section, children: [...] }] }`. */
    COURSE_CATEGORIES: "api/v1/helpers/get-course-categories",
  },

  // Curriculum (boards/classes/subjects)
  BOARDS: {
    LIST: "api/v1/metadata/boards",
    /**
     * Streams of a class. Query params: `institute_id`, `board_id`, `class_id` (the Add Student
     * form sends the ids of its institute, board and class dropdowns). Body: `{ streams: [{
     * id, name, board_id, class_id, institute_id, status }] }`.
     */
    STREAMS: "api/v1/institutions/streams",
    CLASSES: (boardId: number | string) =>
      `api/v1/metadata/boards/${boardId}/classes`,
    SUBJECTS: (boardId: number | string, classId: number | string) =>
      `api/v1/metadata/boards/${boardId}/classes/${classId}/subjects`,
  },
  COURSES: {
    LIST: "/api/v1/content-studio/courses",
    STRUCTURE: "/api/v1/content-studio/courses/structure",
    DETAIL: (courseId: string) => `/api/v1/content-studio/courses/${courseId}`,
    TREE: (courseId: string) => `/api/v1/content-studio/courses/${courseId}/tree`,
    // Note: the API's "sections"/"lessons" path segments correspond to this
    // app's "chapter"/"section" tree node types respectively.
    LESSON_CONTENT: (
      courseId: string,
      chapterId: number | string,
      sectionId: number | string,
    ) => `/api/v1/content-studio/courses/${courseId}/sections/${chapterId}/lessons/${sectionId}`,
    // Granular structure edits. NOTE: the API's "section" == this app's chapter
    // (top-level node); the API's "lesson" == this app's section (child node).
    /** PUT (rename) / DELETE a section (app chapter). */
    SECTION: (courseId: string, sectionId: number | string) =>
      `/api/v1/content-studio/courses/${courseId}/sections/${sectionId}`,
    /** PUT rename a lesson (app section) title. */
    LESSON_TITLE: (
      courseId: string,
      sectionId: number | string,
      lessonId: number | string,
    ) =>
      `/api/v1/content-studio/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}/title`,
    /** DELETE a lesson (app section) via the Content Studio API. */
    LESSON: (
      courseId: string,
      sectionId: number | string,
      lessonId: number | string,
    ) =>
      `/api/v1/content-studio/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}`,
    GENERATE_CONTENT: "/api/v1/content-studio/courses/content",
    GENERATE_IMAGE: "/api/v1/content-studio/courses/generate-image",
    REVISE_STRUCTURE: "/api/v1/content-studio/courses/revise-structure",
    INGESTION_STATUS: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/ingestion-status`,
    INGESTS: "/api/v1/content-studio/courses/ingests",
    INGEST_BY_ID: (textbookId: string) =>
      `/api/v1/content-studio/courses/ingests/${textbookId}`,
    INGEST_STATUS: (textbookId: string) =>
      `/api/v1/content-studio/courses/ingests/${textbookId}/status`,
    UPDATE_COVER_IMAGE: (textbookId: string) =>
      `/api/v1/content-studio/courses/${textbookId}/update-cover-image`,
    AI_REVIEW_INITIATE: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/ai-review/initiate`,
    REVIEW_STATUS: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/review-status`,
    AI_REVIEW_NOTE: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/ai-review/note`,
    HUMAN_REVIEW: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/human-review`,
    SEND_FOR_HUMAN_REVIEW: (courseId: string) =>
      `/api/v1/content-studio/courses/${courseId}/send-for-human-review`,
  },
  // Content Studio
  CONTENT_STUDIO: {
    SUMMARY: "api/v1/content-studio/summary",
    COURSES: "api/v1/content-studio/courses",
    CONTENT_EVALUATION_STATS: "api/v1/content-studio/content-evaluations/stats",
  },

  SYSTEM: {
    EC2_INSTANCE_STATUS: "/api/v1/system/ec2-instance-status",
    START_STOP_EC2_INSTANCE: "/api/v1/system/start-stop-ec2-instance",
  },
  TESTPREP: {
    INGEST: "/api/v1/testprep/ingest",
    INGESTS: "/api/v1/testprep/ingests",
    INGEST_BY_ID: (textbookId: string) =>
      `/api/v1/testprep/ingests/${textbookId}`,
    INGEST_STATUS: "/api/v1/testprep/ingest-status",
    EXAM_TITLES: "/api/v1/testprep/exam-titles",
    EXAMS: "/api/v1/testprep/exams",
    EXAM_BY_ID: (examId: string) => `/api/v1/testprep/exams/${examId}`,
    EXAM_STATUS: (examId: string) => `/api/v1/testprep/exams/${examId}/status`,
  },
  LOOKUP: {
    CONTENT_FORMATS: "/api/v1/admin/content/lookup/content-formats",
    LANGUAGES: "/api/v1/admin/content/lookup/languages",
    PRIMARY_CATEGORIES: "/api/v1/admin/content/lookup/primary-categories",
    SECONDARY_CATEGORIES: "/api/v1/admin/content/lookup/secondary-categories",
    TARGET_AUDIENCES: "/api/v1/admin/content/lookup/target-audiences",
    LEARNING_LEVELS: "/api/v1/admin/content/lookup/learning-levels",
    EDUCATION_LEVELS: "/api/v1/admin/content/lookup/education-levels",
    EXAM_TAGS: "/api/v1/admin/content/lookup/exam-tags",
    LANGUAGE_LEVELS: "/api/v1/admin/content/lookup/language-levels",
    ISSUE_TYPES: "/api/v1/admin/content/lookup/issue-types",
    EDITIONS: "/api/v1/admin/content/lookup/editions",
    REGIONS: "/api/v1/admin/content/lookup/regions",
  },
  ACADEMIC: {
    BOARDS: "/api/v1/metadata/boards",
    BOARD_CLASSES: (boardId: number) =>
      `/api/v1/metadata/boards/${boardId}/classes`,
  },
} as const;
